import cx from 'classnames';
import type { KeyboardEvent, MouseEvent, VoidFunctionComponent } from 'react';
import type { NodeRendererProps } from 'react-arborist';

import Icon, { icons } from '../../generic/Icon';

import type { SnippetTreeNodeData } from './types';

import styles from './Snippets.module.less';

type SnippetNode = NodeRendererProps<SnippetTreeNodeData>['node'];

//  Directories have an arrow indicating if they are open or not
const getArrowIcon = (node: SnippetNode) => {
  if (node.data.type === 'file') {
    return null;
  }

  return (
    <Icon icon={node.isOpen ? icons.chevronDown : icons.chevronRight} />
  );
};

//  Directories get a folder icon, files get a file.
const getNodeIcon = (node: SnippetNode) => {
  if (node.data.type === 'file') {
    return (
      <Icon icon={icons.fileO} />
    );
  }

  return (
    <Icon icon={icons.folderO} />
  );
};

/**
 * SnippetsTreeView - Empty component for displaying snippets in a tree view.
 */
const SnippetsTreeViewNode: VoidFunctionComponent<NodeRendererProps<SnippetTreeNodeData>> = ({
  node,
  style,
}) => {
  const handleClick = (event: MouseEvent) => {
    node.handleClick(event); // built‑in selection logic
    if (node.isInternal) {
      node.toggle();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent default spacebar scroll or button click
      handleClick(event as unknown as MouseEvent);
    }
  };

  return (
    <div
      role="button"
      style={style}
      tabIndex={0}
      className={styles.treeViewNode}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className={cx(styles.treeViewNodeInner, {
        [styles.treeViewNodeInnerSelected]: node.isSelected,
      })}>
        <span className={styles.treeViewNodeArrow}>
          {getArrowIcon(node)}
        </span>
        <span className={styles.treeViewNodeIcon}>
          {getNodeIcon(node)}
        </span>
        <span className={styles.treeViewNodeText}>
          {node.data.name}
        </span>
      </div>
    </div>
  );
};

export default SnippetsTreeViewNode;
